/**
 * Échéance de la session **lisible depuis le navigateur**.
 *
 * Les access tokens sont posés non-httpOnly exactement pour ça : le client doit pouvoir
 * décider *quand* déclencher la rotation, sans attendre le premier 401. Les refresh tokens,
 * eux, restent httpOnly et ne sont jamais lus ici.
 *
 * Deux cookies possibles, dans l'ordre de précédence de `resolveSessionFromKeycloak` : le
 * cookie propre à Catalog (session agence) gagne sur le cookie partagé d'odys (session DMC).
 * Rendre la précédence identique des deux côtés évite de faire tourner la session DMC alors
 * que c'est la session agence qui porte la request.
 *
 * La précédence porte sur un badge **encore valide**, comme côté serveur : le cookie vit 7
 * jours et le token 30 minutes, donc un `catalogToken` présent peut porter une échéance déjà
 * passée. La laisser gagner ferait planifier une rotation sur une session que ce navigateur ne
 * porte plus, pendant que la session DMC — bien vivante — attendrait la sienne.
 */

import { KEYCLOAK_TOKEN_COOKIE } from '@odys/auth/validator';

import { CATALOG_TOKEN_COOKIE } from '@/app/api/lib/auth/session-cookie-names';

const SESSION_COOKIES = [CATALOG_TOKEN_COOKIE, KEYCLOAK_TOKEN_COOKIE] as const;

/**
 * Timestamp d'expiration (ms epoch) du token de session porté par `cookieString`, ou `null`
 * si aucun cookie de session lisible ne porte un `exp` exploitable.
 *
 * Le premier badge encore valide gagne ; à défaut, le premier badge lisible, pour qu'une
 * session dont l'access token vient d'expirer déclenche bien une rotation immédiate.
 *
 * Aucune vérification de signature : la valeur ne sert qu'à *planifier* une rotation, jamais
 * à autoriser quoi que ce soit. Le seul juge de la validité reste `verifyToken`, côté serveur.
 */
export function sessionExpiryFromCookies(cookieString: string): number | null {
    const expiries = SESSION_COOKIES.map((name) => {
        const token = readCookie(cookieString, name);
        return token ? expiryFromJwt(token) : null;
    }).filter((expiry): expiry is number => expiry !== null);

    return expiries.find((expiry) => expiry > Date.now()) ?? expiries[0] ?? null;
}

function readCookie(cookieString: string, name: string): string | null {
    const pair = cookieString
        .split(';')
        .map((entry) => entry.trim())
        .find((entry) => entry.startsWith(`${name}=`));
    return pair ? decodeURIComponent(pair.slice(name.length + 1)) : null;
}

function expiryFromJwt(token: string): number | null {
    const payload = token.split('.')[1];
    if (!payload) return null;

    try {
        const claims: unknown = JSON.parse(
            atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
        ) as unknown;
        const exp = (claims as { exp?: unknown } | null)?.exp;
        return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null;
    } catch {
        return null;
    }
}
